import React from "react";

/**
 * Shared block renderer for the online Event Document viewer.
 * Same block schema as EventDocPDF — paragraph, list, timeline, kv, callout, faq.
 * Tailwind styling that mirrors the platform aesthetic (Apple/Delta premium).
 */

function Paragraph({ block }) {
  const lines = (block.text || "").split("\n").filter(Boolean);
  return (
    <div className="space-y-3">
      {lines.map((l, i) => (
        <p key={i} className="text-[15px] leading-relaxed text-zinc-700">{l}</p>
      ))}
    </div>
  );
}

function BulletList({ block }) {
  return (
    <ul className="space-y-2.5">
      {(block.items || []).map((it, i) => (
        <li key={i} className="flex gap-4 text-[15px] leading-relaxed text-zinc-700">
          <span className="text-[#A39171] shrink-0 select-none">—</span>
          <span className="flex-1">{it}</span>
        </li>
      ))}
    </ul>
  );
}

function ImageBlock({ block }) {
  if (!block?.src) return null;
  return (
    <figure className="border border-zinc-200 bg-white overflow-hidden">
      <img
        src={block.src}
        alt={block.alt || "Event floor plan"}
        className="w-full h-auto block select-none"
        draggable={false}
      />
      {block.caption ? (
        <figcaption className="px-5 py-3 text-xs text-zinc-500 border-t border-zinc-200 uppercase tracking-[0.16em]">
          {block.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

function Timeline({ block }) {
  return (
    <div className="space-y-4">
      {(block.items || []).map((it, i) => (
        <div key={i} className="grid grid-cols-[130px_1fr] gap-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#A39171] pt-1">{it.time}</div>
          <div className="border-l border-zinc-200 pl-5">
            <div className="text-[15px] font-medium text-black">{it.title}</div>
            {it.description ? (
              <div className="text-sm text-zinc-500 leading-relaxed mt-1">{it.description}</div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function KeyValue({ block }) {
  return (
    <dl className="border-t border-zinc-200">
      {(block.pairs || []).map((p, i) => (
        <div key={i} className="grid grid-cols-[160px_1fr] gap-4 border-b border-zinc-200 py-3">
          <dt className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 pt-1">{p.label}</dt>
          <dd className="text-[15px] text-black">{p.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Callout({ block }) {
  const tone = block.tone || "soft";
  const cls =
    tone === "black"
      ? "bg-black text-white border-l-2 border-[#A39171]"
      : tone === "gold"
      ? "bg-[#F5F1EA] border-l-2 border-[#A39171] text-black"
      : "bg-[#FBF9F5] border-l-2 border-[#C6BFAE] text-black";
  const titleCls = tone === "black" ? "text-[#C6B99A]" : "text-[#A39171]";
  return (
    <div className={`${cls} p-5`}>
      {block.title ? (
        <div className={`text-[10px] uppercase tracking-[0.22em] ${titleCls} mb-2`}>{block.title}</div>
      ) : null}
      <div className={`text-[15px] leading-relaxed ${tone === "black" ? "text-white/90" : "text-zinc-800"}`}>
        {block.text}
      </div>
    </div>
  );
}

function Faq({ block }) {
  return (
    <div className="space-y-6">
      {(block.items || []).map((it, i) => (
        <div key={i}>
          <div className="text-[15px] font-medium text-black">{it.q}</div>
          <p className="text-[15px] text-zinc-600 leading-relaxed mt-1.5">{it.a}</p>
        </div>
      ))}
    </div>
  );
}

export function DocumentBlock({ block }) {
  switch (block.type) {
    case "paragraph": return <Paragraph block={block} />;
    case "list":      return <BulletList block={block} />;
    case "timeline":  return <Timeline block={block} />;
    case "kv":        return <KeyValue block={block} />;
    case "callout":   return <Callout block={block} />;
    case "faq":       return <Faq block={block} />;
    case "image":     return <ImageBlock block={block} />;
    default:          return null;
  }
}

export function DocumentSection({ section }) {
  return (
    <section className="border-t border-zinc-200 pt-10">
      <h2 className="serif text-3xl md:text-4xl text-black leading-tight">{section.heading}</h2>
      {section.subheading ? (
        <p className="text-sm text-zinc-500 mt-2">{section.subheading}</p>
      ) : null}
      <div className="mt-6 space-y-6">
        {(section.blocks || []).map((b, i) => (
          <DocumentBlock key={b.id || i} block={b} />
        ))}
      </div>
    </section>
  );
}

export default DocumentBlock;
